import EmojiPicker from "./EmojiPicker";
import { resolveLocationIconEmoji } from "./searchSupport";
import FiltersIcon from "@mui/icons-material/FilterListRounded";
import {
  Avatar,
  Button,
  Chip,
  Card,
  CardContent,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { hasItems } from "common/arrays";
import * as dates from "common/dates";
import { hasValue } from "common/strings";
import DefaultLoader from "components/common/DefaultLoader";
import PriorityChip from "components/common/PriorityChip";
import StatusBadge from "components/common/StatusBadgeV2";
import * as labels from "config.labels";
import moment from "moment";
import { useCallback, useState } from "react";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as staticDataService from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import * as persistenceActions from "store/actions/persistence/persistenceActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import useEmojiTags from "store/hooks/useEmojiTags";
import * as userSelectors from "store/selectors/user/userSelectors";

const MUI_CHIP_COLOR_VALUES = new Set([
  "default",
  "primary",
  "secondary",
  "error",
  "info",
  "success",
  "warning",
]);

const INDUSTRY_CLASS_STYLE = {
  width: "100%",
  "& .industry-name": {
    borderBottom: "none",
    height: "22px",
  },
  "& .industry-name:hover": {
    borderBottom: "1px solid grey",
    height: "22px",
  },
  "& .industry-filter-icon": { opacity: 0 },
  "& .industry-name:hover + .industry-filter-icon": { opacity: 1 },
};

const LayerChip = ({ layer, pill, onClick, expandStatus }) => {
  if (layer == null || layer.limit == null || layer.attachment == null) {
    return <></>;
  }

  const tooltipText = (layer) => {
    const items = [
      layer.reference,
      layer.businessUnitName,
      layer.paperProviderName,
    ].filter((item) => item != null);

    if (items.length === 0) {
      return null;
    } else if (items.length === 1) {
      return items[0];
    } else {
      return items.join(" • ");
    }
  };

  const tooltip = tooltipText(layer);
  const avatar = pill.statusBadge ? (
    <StatusBadge
      status={layer.status}
      variant={expandStatus ? "chip" : "icon"}
    />
  ) : null;

  const chip = (
    <Chip
      avatar={avatar}
      label={layer?.[pill.path]}
      size={"small"}
      variant={pill?.variant ?? "outlined"}
      color={pill?.color}
      onClick={(e) => {
        e.preventDefault();
        onClick(`${pill.prefix ?? pill.path}:"${layer?.[pill.path]}"`);
      }}
    />
  );

  return tooltip == null ? chip : <Tooltip title={tooltip}>{chip}</Tooltip>;
};

const FilterChip = ({
  label,
  pill,
  onClick,
  theme,
  iconMap,
  enableLocationFlagAvatar,
}) => {
  const TooltipWraper = ({ children }) =>
    pill?.tooltip ? (
      <Tooltip title={pill?.tooltip}>{children}</Tooltip>
    ) : (
      <>{children}</>
    );
  const displayLabel = hasValue(label) ? String(label).trim() : label;
  const flagEmoji =
    enableLocationFlagAvatar && hasValue(displayLabel)
      ? resolveLocationIconEmoji(iconMap, displayLabel)
      : null;
  const avatar = flagEmoji ? (
    <Avatar
      sx={{ fontSize: 14, bgcolor: "transparent", width: 24, height: 24 }}
    >
      {flagEmoji}
    </Avatar>
  ) : null;
  const muiChipColor =
    pill?.color != null && MUI_CHIP_COLOR_VALUES.has(pill.color)
      ? pill.color
      : undefined;
  const useBrandAccentFill =
    pill?.color != null && !MUI_CHIP_COLOR_VALUES.has(pill.color);
  return !hasValue(displayLabel) || displayLabel === "-" ? (
    <></>
  ) : (
    <TooltipWraper>
      <Chip
        avatar={avatar}
        label={displayLabel}
        size={"small"}
        variant={pill?.variant ?? "outlined"}
        color={muiChipColor}
        clickable
        onClick={(e) => {
          e.preventDefault();
          onClick(`${pill.prefix ?? pill.path}:"${displayLabel}"`);
        }}
        sx={{
          background: useBrandAccentFill ? theme.brand.accent[500] : null,
          color: useBrandAccentFill ? "white" : null,
        }}
      />
    </TooltipWraper>
  );
};

const PlaceholderChip = () => (
  <Stack mt={2}>
    <Chip
      size={"small"}
      className={"pulse"}
      sx={{ width: "6rem", background: "rgba(0, 0, 0, 0.11)" }}
    />
  </Stack>
);

const Meta = ({
  submission,
  searchConfig,
  isLoading,
  filterClicked,
  expandStatus,
  theme,
  enableUwLocationFlagAvatar,
}) => {
  const numberPeople = submission.people?.people?.length ?? 0;
  if (!hasItems(submission.layers) && numberPeople === 0) {
    return <></>;
  } else if (isLoading) {
    return <PlaceholderChip />;
  }

  const numberLayers = submission.layers.filter(
    (layer) => layer.limit != null && layer.attachment != null
  ).length;

  // We want to display people even if no layers have been set but
  // we don't want to show the current year tag for new submissions
  // which have yet to be filled in (avoids needless clutter)
  if (numberLayers === 0 && numberPeople === 0) {
    return <></>;
  }

  const hasConfiguredLocationPill = (searchConfig?.pills || []).some(
    (pill) => !pill.byLayer && pill.path === "primaryUnderwriterLocation"
  );
  const locationLabel = submission?.primaryUnderwriterLocation?.toString();
  const showAutoLocationChip =
    !hasConfiguredLocationPill &&
    hasValue(locationLabel) &&
    locationLabel !== "-";

  return (
    <Stack
      className={"fade-in"}
      direction={"row"}
      flexWrap={"wrap"}
      gap={0.75}
      mt={2}
    >
      {submission.priority && <PriorityChip priority={submission.priority} />}
      {(searchConfig?.pills || []).map((pill) => {
        if (pill.byLayer) {
          return submission.layers.map((layer) => (
            <LayerChip
              key={layer.id}
              layer={layer}
              pill={pill}
              onClick={filterClicked}
              expandStatus={expandStatus}
            />
          ));
        } else {
          return (
            <FilterChip
              key={pill.path}
              onClick={filterClicked}
              label={submission?.[pill.path]?.toString()}
              pill={pill}
              theme={theme}
              iconMap={searchConfig?.location_config?.iconMap}
              enableLocationFlagAvatar={
                pill.path === "primaryUnderwriterLocation"
                  ? enableUwLocationFlagAvatar
                  : false
              }
            />
          );
        }
      })}
      {showAutoLocationChip && (
        <FilterChip
          key={"primaryUnderwriterLocation-auto"}
          onClick={filterClicked}
          label={locationLabel}
          pill={{ path: "primaryUnderwriterLocation" }}
          theme={theme}
          iconMap={searchConfig?.location_config?.iconMap}
          enableLocationFlagAvatar={enableUwLocationFlagAvatar}
        />
      )}
      <ExpirationPill submission={submission} theme={theme} />
      {(submission.tagNames ?? []).map((tag) => (
        <Chip
          key={`submission-tag-${tag}`}
          label={tag}
          size={"small"}
          clickable={!!filterClicked}
          onClick={(e) => {
            if (!filterClicked) {
              return;
            }
            e.preventDefault();
            filterClicked(`tagNames:"${tag}"`);
          }}
        />
      ))}
    </Stack>
  );
};

const getEffectiveExpiration = (submission) =>
  submission.effectiveExpiration || submission.expiration;

const isExpiringSoon = (submission) => {
  const expiration = getEffectiveExpiration(submission);
  if (!expiration) return false;

  const today = moment.parseZone(dates.getDate()).format("YYYY-MM-DD");
  const endDate = moment.parseZone(today).add(3, "months").format("YYYY-MM-DD");

  return expiration > today && expiration <= endDate;
};

const getDaysUntilExpiration = (submission) => {
  const expiration = getEffectiveExpiration(submission);
  if (!expiration) return null;

  const today = moment.parseZone(dates.getDate());
  const expirationDate = moment.parseZone(expiration);
  const daysRemaining = expirationDate.diff(today, "days");

  return daysRemaining > 0 && daysRemaining < 90 ? daysRemaining : null;
};

const ExpirationPill = ({ submission, theme }) => {
  const daysRemaining = getDaysUntilExpiration(submission);

  if (daysRemaining === null) return null;

  return (
    <Chip
      label={`Expires in ${daysRemaining}d`}
      size={"small"}
      variant={"filled"}
      sx={{ background: theme.brand.accent[500], color: "white" }}
    />
  );
};

const hasNoRenewal = (submission, renewalData) => {
  if (!renewalData?.renewalMap || !submission.submissionId) return false;

  const renewalInfo = renewalData.renewalMap[submission.submissionId];
  return !renewalInfo?.renewals || renewalInfo.renewals.length === 0;
};

const SearchCard = ({
  submission,
  searchConfig,
  configLoading,
  filterClicked,
  renewSubmission,
  renewingSubmission,
  renewError,
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    data: renewalData,
  } = submissionsService.useListRenewalSubmissionsQuery();
  const useImprovedRenewalUI = staticDataService.useFeatureFlag(
    "use-improved-renewal-ui",
    {
      default: true,
    }
  );
  const enableIndustryChipFilter = staticDataService.useFeatureFlag(
    "submission-search-industry-chip-filter",
    {
      default: true,
    }
  );
  const enableEmojiTags = staticDataService.useFeatureFlag(
    "submission-search-emoji-tags",
    {
      default: true,
    }
  );
  const isVisible = useSelector(userSelectors.selectIsVisible);
  const enableUwLocationFlagAvatar =
    isVisible("<Search>.uwLocationFlagAvatar") ?? false;

  const { getTagsForSubmission, toggleTag } = useEmojiTags();
  const submissionEmojis = getTagsForSubmission(submission.submissionId);

  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [isHovered, setIsHovered] = useState(false);

  const createRenewalSubmission = useCallback(() => {
    renewSubmission({
      navigate,
      submissionId: submission.submissionId,
    });
  }, [renewSubmission, submission, navigate]);

  const excludedTransactionTypes = ["MTA", "CANCELLATION", "OPTION"];
  const hasAnyBoundLayer =
    submission.layers?.some((layer) => layer.bound) || false;
  const shouldShowRenewalButton =
    isExpiringSoon(submission) &&
    hasNoRenewal(submission, renewalData) &&
    useImprovedRenewalUI &&
    hasAnyBoundLayer &&
    !excludedTransactionTypes.includes(submission.transactionValue);

  const handleRenewalClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    createRenewalSubmission();
  };

  const industryFilterClicked = (e, industryClass) => {
    if (!enableIndustryChipFilter) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    filterClicked(`industryName:"${industryClass}"`);
  };

  return (
    <Link
      href={"/submissions/" + submission.submissionId}
      target={"_self"}
      underline={"none"}
      {...props}
    >
      <Card
        elevation={0}
        sx={{
          marginBottom: "8px",
          cursor: "pointer",
          "&:hover": {
            boxShadow: "0px 4px 16px rgba(36, 41, 48, 0.1)",
          },
        }}
        onMouseEnter={(_) => setIsHovered(true)}
        onMouseLeave={(_) => setIsHovered(false)}
      >
        <CardContent sx={{ padding: "8px 14px 14px 14px !important" }}>
          <Stack alignItems={"baseline"} gap={0.5} sx={{ width: "100%" }}>
            <Stack sx={{ width: "100%" }}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ width: "100%" }}
              >
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <Typography variant={"subtitle1"} sx={{ fontWeight: 500 }}>
                    {submission.insured}
                  </Typography>
                  {enableEmojiTags && (
                    <EmojiPicker
                      selectedEmojis={submissionEmojis}
                      onToggleEmoji={(emoji) =>
                        toggleTag(submission.submissionId, emoji)
                      }
                    />
                  )}
                </Stack>
                <Typography variant={"caption"} flexShrink={0}>
                  {(submission.reference || "") === "" ? (
                    ""
                  ) : (
                    <span className={"hidden-mobile"}>
                      {submission.reference + " • "}
                    </span>
                  )}
                  {submission.inceptionName}
                  {submission.effectiveDate !== submission.inception &&
                    submission.inception != null &&
                    " (Adj. " + dates.dateName(submission.effectiveDate) + ")"}
                </Typography>
              </Stack>
              {!submission.industryName ? (
                <Skeleton sx={{ fontSize: "14.5px" }} />
              ) : (
                <Stack
                  sx={{
                    ...INDUSTRY_CLASS_STYLE,
                    ...(enableIndustryChipFilter
                      ? {}
                      : {
                          "& .industry-name:hover": { borderBottom: "none" },
                          "& .industry-filter-icon": { display: "none" },
                        }),
                  }}
                  direction={"row"}
                  alignItems={"left"}
                  gap={0.5}
                >
                  <Typography
                    className={"industry-name"}
                    variant={"subtitle2"}
                    mt={-0.5}
                    color={"grey"}
                    onClick={(e) =>
                      industryFilterClicked(e, submission.industryName)
                    }
                  >
                    {submission.industryName}
                  </Typography>
                  <FiltersIcon
                    className={"industry-filter-icon"}
                    fontSize={"small"}
                  />
                </Stack>
              )}
            </Stack>
            {submission.riskDescription && (
              <Typography
                variant={"body2"}
                marginTop={0.5}
                sx={{
                  display: "-webkit-box",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {submission.riskDescription}
              </Typography>
            )}
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={1}
            flexWrap={"wrap"}
          >
            <Meta
              submission={submission}
              isLoading={configLoading}
              searchConfig={searchConfig}
              filterClicked={filterClicked}
              expandStatus={isHovered}
              theme={theme}
              enableUwLocationFlagAvatar={enableUwLocationFlagAvatar}
            />
          </Stack>
          {shouldShowRenewalButton && (
            <Tooltip
              title={
                renewError
                  ? "An error occurred while renewing a submission, please contact support at: support@cactus.bm"
                  : null
              }
            >
              <Button
                variant={"outlined"}
                size={"small"}
                onClick={handleRenewalClick}
                sx={{
                  color: renewError
                    ? theme.brand.red[600]
                    : theme.brand.accent[600],
                  borderColor: renewError
                    ? theme.brand.red[600]
                    : theme.brand.accent[600],
                  backgroundColor: renewError
                    ? theme.brand.red[200]
                    : theme.brand.accent[100],
                  height: "28px",
                  "&:hover": {
                    backgroundColor: renewError
                      ? theme.brand.red[200]
                      : theme.brand.accent[600],
                    color: renewError
                      ? theme.brand.red[600]
                      : theme.brand.white,
                    borderColor: renewError
                      ? theme.brand.red[600]
                      : theme.brand.accent[600],
                  },
                  marginTop: "12px",
                }}
                disabled={renewingSubmission}
              >
                {renewingSubmission ? (
                  <DefaultLoader size={20} />
                ) : (
                  translateLabel(labels.SUBMISSION_LIST_CREATE_RENEWAL_BUTTON, {
                    default: "Create {year} Renewal",
                    replacements: {
                      year: dates.strDateToYearMonthDay(
                        dates.getMoveForwardOneYear(
                          submission.effectiveDate ?? submission.inception
                        )
                      )?.year,
                    },
                  })
                )}
              </Button>
            </Tooltip>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

const mapDispatchToProps = {
  renewSubmission: persistenceActions.renewSubmission,
};

const mapStateToProps = (state) => ({
  renewingSubmission: state.persistence.state === "LOADING",
  renewError: state.persistence.state === "FAILED",
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchCard);
